"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { Customer, Staff } from "@/types";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { APPOINTMENT_STATUS_OPTIONS } from "@/lib/appointmentStatus";

export interface AppointmentFilters {
  status: string;
  search: string;
  customerId: string;
  staffId: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
}

interface AppointmentFilterProps {
  filterStatus: string;
  filters: AppointmentFilters; // Receive filter state from parent
  onFilterChange: (filters: AppointmentFilters) => void;
  customers: Customer[];
  staff: Staff[];
}

export function AppointmentFilter({
  filterStatus,
  filters, // Use filters from parent (controlled component)
  onFilterChange,
  customers,
  staff,
}: AppointmentFilterProps) {
  // No local state - this is now a controlled component
  // All filter values come from the parent via the 'filters' prop

  const handleFilterChange = (key: keyof AppointmentFilters, value: any) => {
    // Validate date range
    if (
      key === "endDate" &&
      value &&
      filters.startDate &&
      value < filters.startDate
    ) {
      toast.error("End date must be after start date");
      return;
    }

    if (
      key === "startDate" &&
      value &&
      filters.endDate &&
      value > filters.endDate
    ) {
      toast.error("Start date must be before end date");
      return;
    }

    const newFilters = { ...filters, [key]: value };
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const clearedFilters: AppointmentFilters = {
      status: "all",
      search: "",
      customerId: "all",
      staffId: "all",
      startDate: undefined,
      endDate: undefined,
    };
    onFilterChange(clearedFilters);
  };

  const hasActiveFilters =
    filters.status !== "all" ||
    filters.search !== "" ||
    filters.customerId !== "all" ||
    filters.staffId !== "all" ||
    filters.startDate !== undefined ||
    filters.endDate !== undefined;

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Filters</h3>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-8"
              >
                <X className="h-4 w-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <Label>Search</Label>
              <Input
                placeholder="Search by customer name or vehicle plate..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </div>

            {/* Status Filter */}
            <div>
              <Label>Status</Label>
              <Select
                value={filters.status}
                onValueChange={(value) => handleFilterChange("status", value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {APPOINTMENT_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Customer Filter */}
            <div>
              <Label>Customer</Label>
              <Select
                value={filters.customerId}
                onValueChange={(value) =>
                  handleFilterChange("customerId", value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customers</SelectItem>
                  {[...customers].sort((a, b) => a.name.localeCompare(b.name)).map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Staff Filter */}
            <div>
              <Label>Service Provider</Label>
              <Select
                value={filters.staffId}
                onValueChange={(value) => handleFilterChange("staffId", value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {[...staff].sort((a, b) => a.name.localeCompare(b.name)).map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div>
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !filters.startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.startDate ? (
                      format(filters.startDate, "PPP")
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.startDate}
                    onSelect={(date) => handleFilterChange("startDate", date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* End Date */}
            <div>
              <Label>End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !filters.endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.endDate ? (
                      format(filters.endDate, "PPP")
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.endDate}
                    onSelect={(date) => handleFilterChange("endDate", date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
